import { SortEnumValues, type SortEnum } from "@superbright/indexeddb-orm";
import { useState, useRef } from "react";

import { Icon, Typography } from "components";
import { useOutsideClick } from "utils/hooks";

import { useDropdownDirection } from "./hook/useDropdownDirection"; // Import hook
import styles from "./SortDropdown.module.css";

export interface SortOption {
  label: string;
  value: SortEnum;
}

export interface SortTrackingPayload {
  sortEnum: SortEnum;
  sortIndex: number;
}

export interface TrackingEvents {
  trackOpenSort?: (data: SortTrackingPayload) => void;
  trackSelectSort?: (data: SortTrackingPayload) => void;
  trackCancelSort?: (data: SortTrackingPayload) => void;
}

export interface SortDropdownProps {
  options: SortOption[];
  selected: string;
  onChange: (value: string) => void;
  trackingEvents?: TrackingEvents; // <-- optional, injected from parent
}

export const SortDropdown = ({
  options,
  selected,
  onChange,
  trackingEvents,
}: SortDropdownProps) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((opt) => opt.value === selected);

  const getSortTrackingPayload = (
    sortValue?: string
  ): SortTrackingPayload | null => {
    if (!sortValue) return null;
    if (!SortEnumValues.includes(sortValue as SortEnum)) return null;

    const sortEnum = sortValue as SortEnum;
    const uiIndex = options.findIndex((opt) => opt.value === sortEnum);
    const canonicalIndex = SortEnumValues.indexOf(sortEnum);
    const sortIndex = uiIndex >= 0 ? uiIndex : canonicalIndex;

    if (sortIndex < 0) return null;

    return { sortEnum, sortIndex };
  };

  const { direction, ready } = useDropdownDirection(
    open,
    triggerRef,
    dropdownRef
  );

  useOutsideClick(dropdownRef, () => {
    const payload = getSortTrackingPayload(selectedOption?.value ?? selected);
    if (payload) {
      trackingEvents?.trackCancelSort?.(payload);
    }

    setOpen(false);
  });

  const handleToggle = () => {
    setOpen((prev) => {
      const next = !prev;
      if (!prev && next) {
        const payload = getSortTrackingPayload(
          selectedOption?.value ?? selected
        );
        if (payload) {
          trackingEvents?.trackOpenSort?.(payload);
        }
      }
      return next;
    });
  };

  return (
    <div className={styles.container}>
      <button
        ref={triggerRef}
        className={styles.trigger}
        onClick={handleToggle}
      >
        Sort by:{" "}
        <span className={styles.selected}>{selectedOption?.label}</span>
      </button>

      {open && (
        <div
          ref={dropdownRef}
          className={`${styles.dropdown} ${styles[direction]} ${
            ready ? styles.open : styles.measuring
          }`}
        >
          <div className={styles.tail}>
            <Icon name="tooltip-tail" />
          </div>
          <div className={styles.label}>
            <Typography variant="small" weight="medium">
              Sort by
            </Typography>
          </div>
          {options.map((option) => (
            <button
              key={option.value}
              className={`${styles.option} ${
                option.value === selected ? styles.active : ""
              }`}
              onClick={() => {
                onChange(option.value);
                const payload = getSortTrackingPayload(option.value);
                if (payload) {
                  trackingEvents?.trackSelectSort?.(payload);
                }
                setOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
